'use client'

// Auto BreadcrumbList JSON-LD, derived from the URL on the client.
//
// This used to be a server component reading the middleware-set
// `x-pathname` header — but `headers()` is a dynamic API, and calling
// it from the root layout silently flipped EVERY route in the app to
// per-request SSR (the Aug-2026 crawl's "101 slow pages" finding: the
// whole site had zero static routes). A root layout cannot know the
// pathname statically, so the auto-derived breadcrumbs now render
// client-side via usePathname(), which Google and Bing both execute.
// Everything else this component used to emit is either static
// (Speakable, now directly in the layout) or page-specific
// (FAQ / Product / Review schema, now emitted server-side by the pages
// themselves so non-JS AI crawlers still see it in the HTML).

import { usePathname } from 'next/navigation'

const SITE = 'https://idukki.io'

// Human-friendly label per path segment. Falls back to a title-cased
// version of the slug — only override when that transform would be wrong.
const SEGMENT_LABELS: Record<string, string> = {
  blog: 'Blog',
  author: 'Author',
  features: 'Features',
  integrations: 'Integrations',
  industries: 'Industries',
  compare: 'Compare',
  vs: 'Compare',
  pricing: 'Pricing',
  enterprise: 'Enterprise',
  security: 'Security',
  about: 'About',
  contact: 'Contact',
  tools: 'Tools',
  resources: 'Resources',
  showcase: 'Showcase',
  glossary: 'Glossary',
  'case-studies': 'Case studies',
  widgets: 'Widgets',
  help: 'Help',
}

function labelFor(segment: string): string {
  if (SEGMENT_LABELS[segment]) return SEGMENT_LABELS[segment]
  return segment
    .split('-')
    .map((w) => (w.length <= 3 ? w.toUpperCase() : w[0].toUpperCase() + w.slice(1)))
    .join(' ')
}

export function AutoPageJsonLd() {
  const pathname = usePathname() || '/'
  if (pathname === '/') return null

  const segments = pathname.split('/').filter(Boolean)
  const items = [{ name: 'Home', href: '/' }]
  let acc = ''
  for (const seg of segments) {
    acc += '/' + seg
    items.push({ name: labelFor(seg), href: acc })
  }
  if (items.length <= 1) return null

  const data = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.name,
      item: `${SITE}${c.href === '/' ? '' : c.href}`,
    })),
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  )
}
